

// ============================================
// components/porchlight/PorchlightSettingsModal.js
// ============================================
import React, { useState, useEffect } from 'react';
import {
  Modal, Paper, Box, Typography, TextField, Button,
  Checkbox, FormControlLabel, Divider, IconButton, CircularProgress,
  Select, MenuItem, FormControl
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DURATION_OPTIONS, ERRORS } from '../../utils/constants';
import { getFriendDisplayName } from '../../utils/helpers';

// Visually hidden style for accessible labels
const visuallyHiddenStyle = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
};

const PorchlightSettingsModal = ({ 
  open, 
  onClose, 
  friends, 
  onConfirm, 
  isNewSession,
  initialDuration = DURATION_OPTIONS.find(opt => opt.value === 60)?.value || DURATION_OPTIONS[0].value,
  initialSelectedFriends = []
}) => {
  const [duration, setDuration] = useState(String(initialDuration));
  const [selectedFriends, setSelectedFriends] = useState(initialSelectedFriends);
  const [selectAll, setSelectAll] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debugFriends = (() => {
    try { return typeof window !== 'undefined' && window.localStorage.getItem('DEBUG_FRIENDS') === 'true'; } catch (_) { return false; }
  })();

  useEffect(() => {
    if (open && isNewSession) {
      setDuration(String(DURATION_OPTIONS.find(opt => opt.value === 60)?.value || DURATION_OPTIONS[0].value));
      setSelectedFriends(friends.map(f => f.id));
      setSelectAll(true);
      setError('');
    }
  }, [open, isNewSession, friends]);

  const handleConfirm = async () => {
    const minutes = Number(duration);
    if (!duration || isNaN(minutes) || minutes < 1 || minutes > 720) {
      setError(ERRORS.INVALID_DURATION);
      return;
    }
    
    setLoading(true);
    setError('');
    await onConfirm(minutes, selectedFriends);
    setLoading(false);
  };

  const handleFriendToggle = (friendId) => {
    setSelectedFriends(prev => {
      const newSelection = prev.includes(friendId) 
        ? prev.filter(id => id !== friendId)
        : [...prev, friendId];
      setSelectAll(newSelection.length === friends.length);
      return newSelection;
    });
  };

  const handleSelectAllToggle = () => {
    setSelectedFriends(selectAll ? [] : friends.map(f => f.id));
    setSelectAll(!selectAll);
  };

  return (
    <Modal 
      open={open} 
      onClose={loading ? undefined : onClose}
      aria-labelledby="pl-settings-title"
      aria-modal="true"
      role="dialog"
      hideBackdrop={typeof window !== 'undefined' && !!window.Cypress}
    >
      <Paper 
        elevation={0}
        variant="glass"
        className="modal-animated" 
        data-testid="porchlight-settings-modal"
        sx={{ 
          p: 3, 
          maxWidth: 500, 
          margin: 'auto', 
          marginTop: '15%', 
          position: 'relative' 
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={loading}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
          aria-label="Close settings"
        >
          <CloseIcon />
        </IconButton>
        
        <Typography id="pl-settings-title" variant="h6" component="h2" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }}>
          Settings for your porchlight
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {/* Friends Selection */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#222', fontWeight: 600 }}>
              Visible to:
            </Typography>
            <Box sx={{ 
              border: '1px solid #ffe9a8', 
              borderRadius: 1, 
              p: 1,
              maxHeight: 200,
              overflowY: 'auto',
              bgcolor: '#fffef7'
            }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={selectAll}
                    onChange={handleSelectAllToggle}
                    sx={{ color: '#222' }}
                    inputProps={{ 'aria-label': 'Select all friends', 'data-testid': 'all-friends-checkbox' }}
                  />
                }
                label={<span style={visuallyHiddenStyle}>Select all friends</span>}
                sx={{ mb: 1, alignItems: 'center' }}
              />
              <Divider sx={{ mb: 1 }} />
              {friends.map((friend) => {
                const name = getFriendDisplayName(friend);
                if (debugFriends) {
                  try {
                    // eslint-disable-next-line no-console
                    console.debug('[PorchlightSettingsModal] Friend option', {
                      id: friend.id,
                      friendUid: friend.friendUid,
                      friendName: friend.friendName,
                      firstName: friend.firstName,
                      lastName: friend.lastName,
                      email: friend.email,
                      computedName: name,
                    });
                  } catch (_) {}
                }
                return (
                  <FormControlLabel
                    key={friend.id}
                    control={
                      <Checkbox 
                        checked={selectedFriends.includes(friend.id)}
                        onChange={() => handleFriendToggle(friend.id)}
                        sx={{ color: '#222' }}
                        inputProps={{ 'data-testid': `friend-checkbox-${friend.id}` }}
                      />
                    }
                    label={
                      <Typography sx={{ color: '#222', fontSize: '0.9rem' }}>
                        {name}
                      </Typography>
                    }
                    sx={{ mb: 0.5, alignItems: 'center' }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Duration */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#222', fontWeight: 600 }}>
              Duration:
            </Typography>
            <FormControl fullWidth disabled={loading}>
              <Select
                value={duration}
                onChange={(e) => setDuration(String(e.target.value))}
                displayEmpty
                SelectDisplayProps={{ 'data-testid': 'duration-select' }}
              >
                {DURATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={String(opt.value)} data-testid={`duration-option-${opt.value}`}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mt: 2, fontSize: '0.9rem' }}>
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            onClick={onClose} 
            disabled={loading} 
            className="button-animated"
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleConfirm}
            disabled={loading || (!selectAll && selectedFriends.length === 0)}
            className="button-animated"
            data-testid="confirm-porchlight"
          >
            {loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              isNewSession ? 'Turn ON My Porchlight' : 'Update Settings'
            )}
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default PorchlightSettingsModal;
